import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { PasswordStrength, isStrongPassword } from "@/components/PasswordStrength";
import { ShieldCheck } from "lucide-react";

export default function ForcePasswordChange({ onDone, onLogout }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isStrongPassword(pw)) return setError("Kies een wachtwoord van minimaal 8 tekens met minstens 1 cijfer.");
    if (pw !== pw2) return setError("De twee wachtwoorden zijn niet gelijk.");
    setBusy(true);
    try {
      const r = await api.post("/auth/change-password", { new_password: pw });
      if (r.data && r.data.token) localStorage.setItem("clazing_token", r.data.token);
      toast.success("Je wachtwoord is ingesteld. Gebruik dit voortaan om in te loggen.");
      onDone();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="force-pw-overlay" className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0c2537]/95 p-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-5 flex flex-col items-center text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100"><ShieldCheck className="h-6 w-6 text-[#1C64B0]" /></div>
          <h1 className="text-xl font-bold text-slate-900">Kies je eigen wachtwoord</h1>
          <p className="mt-1 text-sm text-slate-500">Voor je veiligheid stel je eenmalig een persoonlijk wachtwoord in. Dit blijft voortaan behouden, ook na updates.</p>
        </div>
        {error && <div data-testid="force-pw-error" className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-semibold text-slate-600">Nieuw wachtwoord</Label>
            <PasswordInput data-testid="force-pw-new" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1" toggleTestId="force-pw-new-toggle" autoFocus required />
            <PasswordStrength value={pw} />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Herhaal wachtwoord</Label>
            <PasswordInput data-testid="force-pw-confirm" value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1" toggleTestId="force-pw-confirm-toggle" required />
          </div>
        </div>
        <Button data-testid="force-pw-submit" type="submit" disabled={busy} className="mt-6 w-full bg-[#1C64B0] hover:bg-[#154E8A]">{busy ? "Bezig..." : "Wachtwoord instellen"}</Button>
        <button type="button" data-testid="force-pw-logout" onClick={onLogout} className="mt-3 w-full text-center text-xs text-slate-400 hover:text-slate-600 hover:underline">Uitloggen</button>
      </form>
    </div>
  );
}
